import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useLocation } from "wouter";
import { loginSchema, type LoginInput } from "@shared/validation/auth.schema";
import { useLogin, useVerifyMfa, isMfaChallenge } from "../lib/auth/useAuth";
import { ApiError } from "../lib/api/client";
import { INPUT_CLASS } from "../lib/forms/inputClass";
import { AuthHero } from "../components/AuthHero";
import { BrandWordmark } from "../components/BrandWordmark";
import { Button } from "../components/ui/Button";
import { Field } from "../components/ui/Field";
import { Reveal } from "../components/ui/Reveal";

export function Login() {
  const [, setLocation] = useLocation();
  const loginMutation = useLogin();
  const verifyMutation = useVerifyMfa();
  // Set when the password was correct but the account needs a second factor.
  // The password is NOT retained — the server holds the pending state in the
  // session, so nothing sensitive lives in component state here.
  const [awaitingMfa, setAwaitingMfa] = useState(false);
  const [mfaCode, setMfaCode] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginInput) => {
    try {
      const result = await loginMutation.mutateAsync(data);
      if (isMfaChallenge(result)) {
        setAwaitingMfa(true);
        return;
      }
      setLocation("/dashboard");
    } catch {
      /* surface via loginMutation.error */
    }
  };

  const onVerify = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      await verifyMutation.mutateAsync({ code: mfaCode });
      setLocation("/dashboard");
    } catch {
      /* surface via verifyMutation.error */
    }
  };

  const serverError = loginMutation.error;

  return (
    <main className="min-h-screen lg:grid lg:grid-cols-2">
      <AuthHero />

      <div className="flex min-h-screen flex-col bg-white">
        <header className="px-6 py-5 lg:hidden">
          <Link href="/" className="inline-flex items-center gap-2">
            <BrandWordmark className="h-6" />
          </Link>
        </header>

        <div className="flex flex-1 items-center justify-center px-6 py-12">
          <Reveal className="w-full max-w-sm">
            <h1 className="font-display text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl">
              {awaitingMfa ? "One more step" : "Welcome back"}
            </h1>
            <p className="mt-1.5 text-sm text-neutral-600">
              {awaitingMfa
                ? "Enter the 6-digit code from your authenticator app, or one of your backup codes."
                : "Sign in to your Leena's List account."}
            </p>

            {awaitingMfa ? (
              <form onSubmit={onVerify} className="mt-6 space-y-4">
                <Field label="Verification code" htmlFor="mfa-code">
                  <input
                    id="mfa-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    className={INPUT_CLASS}
                    placeholder="123456"
                  />
                </Field>

                {verifyMutation.error instanceof ApiError && (
                  <p className="rounded-xl border border-red-100 bg-red-50 p-3 text-sm text-red-700">
                    {verifyMutation.error.code === "RATE_LIMITED"
                      ? "Too many attempts. Please wait a few minutes."
                      : verifyMutation.error.message}
                  </p>
                )}

                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  isLoading={verifyMutation.isPending}
                >
                  {verifyMutation.isPending ? "Verifying…" : "Verify"}
                </Button>

                <button
                  type="button"
                  onClick={() => {
                    setAwaitingMfa(false);
                    setMfaCode("");
                  }}
                  className="w-full text-sm text-neutral-600 hover:text-neutral-900"
                >
                  Start over
                </button>
              </form>
            ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
              <Field label="Email" htmlFor="email" error={errors.email?.message}>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  {...register("email")}
                  className={INPUT_CLASS}
                />
              </Field>

              <Field label="Password" htmlFor="password" error={errors.password?.message}>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  {...register("password")}
                  className={INPUT_CLASS}
                />
              </Field>

              <p className="-mt-1 text-right text-sm">
                <Link href="/forgot-password" className="font-medium text-sky-700 hover:underline">
                  Forgot your password?
                </Link>
              </p>

              {serverError instanceof ApiError && (
                <p className="rounded-xl border border-red-100 bg-red-50 p-3 text-sm text-red-700">
                  {serverError.code === "RATE_LIMITED"
                    ? "Too many attempts. Please wait a few minutes."
                    : "Invalid email or password."}
                </p>
              )}

              <Button
                type="submit"
                size="lg"
                className="w-full"
                isLoading={isSubmitting || loginMutation.isPending}
              >
                {loginMutation.isPending ? "Signing in…" : "Sign in"}
              </Button>
            </form>
            )}

            <p className="mt-6 text-sm text-neutral-600">
              New to Leena's List?{" "}
              <Link href="/register" className="font-medium text-sky-700 hover:text-sky-800">
                Create an account
              </Link>
            </p>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
